import type {
  LearningJourneyLessonDraft,
  LearningJourneyOperation,
} from "@repo/app-types/entities/configurationAgent";
import {
  getOperationCategory,
  getOperationLabel,
} from "@repo/app-types/helpers/configurationAgentParser";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import {
  IconCheck,
  IconCircleMinus,
  IconCirclePlus,
  IconPencil,
} from "@tabler/icons-react";
import { useState, type FC, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { useConfigurationAssistantContext } from "./configurationAssistantContext.js";

export interface LearningJourneyOperationCardProps {
  operationId: string;
  operation: LearningJourneyOperation;
}

type UpdateLessonOperation = Extract<
  LearningJourneyOperation,
  { type: "update-lesson" }
>;
type OperationDetail = {
  label: string;
  value: ReactNode;
};

export const LearningJourneyOperationCard: FC<
  LearningJourneyOperationCardProps
> = ({ operationId, operation }) => {
  const { applyOperation, isOperationApplied, getEntityName } =
    useConfigurationAssistantContext();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const category = getOperationCategory(operation);
  const label = getOperationLabel(operation);
  const isApplied = isOperationApplied(operationId);

  const handleApply = async () => {
    setIsLoading(true);
    setErrorMessage(undefined);
    try {
      await applyOperation(operationId, operation);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Unable to apply this change.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const details = getOperationDetails(operation, getEntityName);

  return (
    <div
      className={twMerge(
        "overflow-hidden rounded-xl border border-border-light bg-white shadow-xs",
        category === "add" && "border-emerald-200",
        category === "update" && "border-blue-200",
        category === "remove" && "border-rose-200",
      )}
    >
      <div
        className={twMerge(
          "flex items-center gap-2 px-3 py-2",
          category === "add" && "bg-emerald-50",
          category === "update" && "bg-blue-50",
          category === "remove" && "bg-rose-50",
        )}
      >
        <OperationIcon category={category} />
        <span
          className={twMerge(
            "text-xs font-semibold",
            category === "add" && "text-emerald-700",
            category === "update" && "text-blue-700",
            category === "remove" && "text-rose-700",
          )}
        >
          {label}
        </span>
      </div>

      <div className="space-y-2 px-3 py-2.5">
        {details.map((detail, index) => (
          <div key={`${detail.label}-${index}`}>
            <div className="text-[10px] font-medium text-muted-foreground">
              {detail.label}
            </div>
            <div
              className={twMerge(
                "mt-0.5 text-[13px] leading-relaxed whitespace-pre-wrap text-foreground",
                category === "remove" && "line-through",
              )}
            >
              {detail.value}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-2 border-t border-gray-100 px-3 py-2">
        {errorMessage ? (
          <div className="rounded-md bg-red-50 px-2 py-1.5 text-xs text-red-700">
            {errorMessage}
          </div>
        ) : null}

        <button
          type="button"
          onClick={handleApply}
          disabled={isApplied || isLoading}
          className={twMerge(
            "flex w-full items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-medium transition-colors",
            isApplied || isLoading
              ? "cursor-default bg-gray-100 text-muted-foreground"
              : twMerge(
                  category === "add" &&
                    "bg-emerald-100 text-emerald-700 hover:bg-emerald-200",
                  category === "update" &&
                    "bg-blue-100 text-blue-700 hover:bg-blue-200",
                  category === "remove" &&
                    "bg-rose-100 text-rose-700 hover:bg-rose-200",
                ),
          )}
        >
          {isLoading ? (
            <>
              <Loader
                style={LoaderStyle.SPINNER}
                className="size-3.5 fill-gray-500"
              />
              Applying change
            </>
          ) : isApplied ? (
            <>
              <IconCheck className="size-3.5" />
              Applied
            </>
          ) : (
            "Apply"
          )}
        </button>
      </div>
    </div>
  );
};

const OperationIcon: FC<{ category: "add" | "update" | "remove" }> = ({
  category,
}) => {
  const iconClass = twMerge(
    "size-3.5",
    category === "add" && "text-emerald-600",
    category === "update" && "text-blue-600",
    category === "remove" && "text-rose-600",
  );

  switch (category) {
    case "add":
      return <IconCirclePlus className={iconClass} />;
    case "update":
      return <IconPencil className={iconClass} />;
    case "remove":
      return <IconCircleMinus className={iconClass} />;
  }
};

function getOperationDetails(
  operation: LearningJourneyOperation,
  getEntityName: (
    id: string,
    type: "section" | "criteria" | "test-case" | "chapter" | "lesson",
  ) => string | undefined,
): OperationDetail[] {
  switch (operation.type) {
    case "add-chapter": {
      const bundledLessons = operation.lessons ?? [];
      return [
        {
          label: "Topic",
          value: operation.topic ?? "Untitled chapter",
        },
        ...(bundledLessons.length
          ? [
              {
                label: "Included lessons",
                value: formatBundledLessonSummary(bundledLessons),
              },
            ]
          : []),
      ];
    }
    case "update-chapter":
      return [
        {
          label: "Chapter",
          value:
            getEntityName(operation.id, "chapter") ??
            operation.topic ??
            "Selected chapter",
        },
        ...(operation.topic
          ? [{ label: "New topic", value: operation.topic }]
          : []),
      ];
    case "remove-chapter":
      return [
        {
          label: "Chapter",
          value: operation.name,
        },
      ];
    case "move-chapter":
      return [
        {
          label: "Chapter",
          value: getEntityName(operation.id, "chapter") ?? "Selected chapter",
        },
        {
          label: "Move",
          value: `${operation.position} ${getEntityName(operation.targetChapterId, "chapter") ?? "target chapter"}`,
        },
      ];
    case "add-lesson":
      return [
        {
          label: "Chapter",
          value:
            getEntityName(operation.chapterId, "chapter") ?? "Selected chapter",
        },
        {
          label: "Lesson type",
          value: getLessonTypeLabel(operation.lessonType),
        },
        {
          label: "Lesson details",
          value: formatLessonSummary(operation),
        },
      ];
    case "update-lesson":
      return [
        {
          label: "Lesson",
          value:
            getEntityName(operation.id, "lesson") ??
            operation.title ??
            "Lesson",
        },
        ...(operation.title
          ? [{ label: "Title", value: operation.title }]
          : []),
        ...(operation.topic
          ? [{ label: "Topic", value: operation.topic }]
          : []),
        ...(hasLessonContentChanges(operation)
          ? [
              {
                label: "Updated details",
                value: formatUpdatedLessonSummary(operation),
              },
            ]
          : []),
      ];
    case "remove-lesson":
      return [
        {
          label: "Lesson",
          value: operation.name,
        },
      ];
    case "move-lesson":
      return [
        {
          label: "Lesson",
          value: getEntityName(operation.id, "lesson") ?? "Selected lesson",
        },
        {
          label: "Move",
          value:
            operation.position === "end"
              ? `to the end of ${getEntityName(operation.targetChapterId ?? "", "chapter") ?? "target chapter"}`
              : `${operation.position} ${getEntityName(operation.targetLessonId ?? "", "lesson") ?? "target lesson"}`,
        },
      ];
  }
}

function formatBundledLessonSummary(
  lessons: readonly LearningJourneyLessonDraft[],
) {
  return lessons
    .map((lesson, index) => {
      const lessonSummary = formatLessonSummary(lesson);
      return `${index + 1}. ${lessonSummary}`;
    })
    .join("\n\n");
}

function formatLessonSummary(lesson: LearningJourneyLessonDraft) {
  const lessonName = lesson.title ?? lesson.topic ?? "Untitled lesson";
  const lines = [`${getLessonTypeLabel(lesson.lessonType)}: ${lessonName}`];

  if (lesson.questions?.length) {
    lines.push(...formatQuestionLines(lesson.questions));
  }

  if (lesson.participantInstructions) {
    lines.push(
      `Trainee instructions: ${formatInstructionPreview(lesson.participantInstructions)}`,
    );
  }

  if (lesson.agentInstructions) {
    lines.push(
      `AI agent instructions: ${formatInstructionPreview(lesson.agentInstructions)}`,
    );
  }

  return lines.join("\n");
}

function formatUpdatedLessonSummary(operation: UpdateLessonOperation) {
  const lines: string[] = [];

  if (operation.questions?.length) {
    lines.push(...formatQuestionLines(operation.questions));
  }

  if (operation.participantInstructions) {
    lines.push(
      `Trainee instructions: ${formatInstructionPreview(operation.participantInstructions)}`,
    );
  }

  if (operation.agentInstructions) {
    lines.push(
      `AI agent instructions: ${formatInstructionPreview(operation.agentInstructions)}`,
    );
  }

  return lines.join("\n");
}

function formatQuestionLines(questions: readonly string[]) {
  const previewQuestions = questions
    .slice(0, 3)
    .map((question) => `- ${question}`);
  if (questions.length > 3) {
    previewQuestions.push(`- ${questions.length - 3} more questions`);
  }

  return [`Questions (${questions.length}):`, ...previewQuestions];
}

function formatInstructionPreview(content: string) {
  const normalized = content.replace(/\s+/g, " ").trim();
  if (normalized.length <= 140) {
    return normalized;
  }

  return `${normalized.slice(0, 137)}...`;
}

function getLessonTypeLabel(
  lessonType: LearningJourneyLessonDraft["lessonType"],
) {
  switch (lessonType) {
    case "chat-quiz":
      return "Chat quiz";
    case "knowledge-test":
      return "Knowledge test";
    case "role-play":
      return "Role play";
  }
}

function hasLessonContentChanges(operation: UpdateLessonOperation) {
  return Boolean(
    operation.questions?.length ||
    operation.participantInstructions ||
    operation.agentInstructions,
  );
}
